import { Helmet } from 'react-helmet-async';
import SearchControls from '../SearchControls';
import { JourneyList } from '../JourneyList';
import { Message } from '../Message';
import { JourneysScreenProps } from '../../types/search';
import { useUrlSync } from '../../hooks/useUrlSync';
import { SearchParams } from '../../utils/urlParams';
import { TaggedJourney } from '../../utils/mapJourneysData';

const JourneysScreen: React.FC<JourneysScreenProps> = ({
  journeys,
  loading,
  hasError,
  errorMessage,
  earlierThan,
  laterThan,
  handleFetchJourneys,
  setOrigin,
  setDestination,
  setOriginName,
  setDestinationName,
  setDate,
  setTripType,
  setOptions,
  originId,
  destinationId,
  originName,
  destinationName,
  date,
  tripType,
  options,
}) => {
  // Sync URL parameters with state
  useUrlSync({
    searchState: {
      originId,
      destinationId,
      originName,
      destinationName,
      date,
      tripType,
      options,
    },
    journeys,
    onStateUpdate: (params: SearchParams) => {
      setOrigin(params.originId);
      setDestination(params.destinationId);
      setOriginName(params.originName);
      setDestinationName(params.destinationName);
      setDate(params.date);
      setTripType(params.tripType);
      setOptions(params.options || {});
    },
    onFetch: () => {
      handleFetchJourneys('initial');
    },
  });

  const helmetTitle = originName && destinationName
    ? `${originName} → ${destinationName} | Train Journeys | chuuchuu`
    : 'Train Journeys | chuuchuu';
  const helmetDesc = originName && destinationName
    ? `Find the most reliable train from ${originName} to ${destinationName}. Real-time delay predictions and punctuality scores backed by European rail data.`
    : 'Search train journeys across Europe with real-time delay predictions and punctuality scores.';

  return (
    <>
      <Helmet>
        <title>{helmetTitle}</title>
        <meta name="description" content={helmetDesc} />
      </Helmet>
      <SearchControls
        setOrigin={setOrigin}
        setDestination={setDestination}
        setOriginName={setOriginName}
        setDestinationName={setDestinationName}
        setDate={setDate}
        setTripType={setTripType}
        setOptions={setOptions}
        fetchJourneys={handleFetchJourneys}
        screen='journeys'
        originId={originId}
        destinationId={destinationId}
        originName={originName}
        destinationName={destinationName}
        date={date}
        tripType={tripType}
        options={options}
      />
      {loading && <Message type="loading" message="" />}
      {!hasError && (
        <JourneyList
          journeys={journeys}
          earlierThan={earlierThan}
          laterThan={laterThan}
          loading={loading}
          fetchJourneys={handleFetchJourneys}
          originName={originName}
          destinationName={destinationName}
          date={date}
          tripType={tripType}
        />
      )}
      {hasError && (
        <Message
          type={errorMessage === 'no-journeys' || errorMessage?.startsWith('no-journeys|') ? 'no-journeys' : 'error'}
          message={errorMessage}
        />
      )}
    </>
  );
};

export default JourneysScreen;
